import {
    AbstractTableVizComponent,
    ColumnRenderers,
    TableItem
} from "../../report/view/visualizations/table/AbstractTableViz";
import {
    horizontalBar,
    labelHeader,
    numberTrendRenderer,
    percentRenderer,
    satScoreRenderer,
    volumeHeaderRenderer,
    volumePercentHeaderRenderer,
    volumeRenderer
} from "./TableRenderers";
import {TableDatasetDisplay} from "./Table";
import classnames from "classnames";
import React from "react";
import {VisualizationInstance} from "../../report/model/viz/VisualizationInstance";
import {services} from "../../application/service/services";
import _ from "lodash";
import colors from '../../style/_chartColors.module.scss';
import type {TFunction} from "i18next";

export class DataRenderer {
    viz: VisualizationInstance;
    t: TFunction;


    constructor(viz: VisualizationInstance, t: TFunction) {
        this.viz = viz;
        this.t = t;
    }

    dataColumnRenderers: ColumnRenderers = {
        ["opinions"]: {
            editorLabel: 'dashboard.viz.table.opinions',
            headerRenderer: labelHeader('dashboard.viz.table.opinions', false),
            dataRenderer: this.opinionsAndTonalitiesDataRenderer.bind(this),
            valueGetters: {
                "positive": (item) => item.value.POSITIVE,
                "negative": (item) => item.value.NEGATIVE
            }
        }
        ,
        ["distribution"]:
            {
                editorLabel: (column: TableDatasetDisplay<any>) => {
                    return services.getFieldsService().getField(column.columnName) ? services.getFieldsService().getField(column.columnName).getLabel(this.t) : column.columnName;
                },
                dataRenderer: ((rowValue, column: TableDatasetDisplay<any>) => {
                    const showPercentages = this.viz.definition.uiParams.columns?.[column.columnName]?.showPercentages
                    return horizontalBar((rowValue, column) => {
                        const columnValue = rowValue.data.filter(columnValue => columnValue.name === column.columnName).find(() => true);
                        const distributionResult = [];
                        if (columnValue && columnValue.value) {
                            const values = columnValue.value;

                            const columnDef = this.viz.definition.params.columns.find(currentColumn => currentColumn.name == column.columnName)
                            for (const property in values) {
                                let color = null;
                                if (columnDef) {
                                    if (columnDef.values) {
                                        const columnDefValue = Object.entries(columnDef.values)
                                            .find(([columnDefStringValue, columnDefProps]) => columnDefStringValue === property)
                                        color = columnDefValue ? columnDefValue[1]['color'] : null;
                                    }
                                }
                                if (!color) {
                                    color = services.getColorService().getColorForFieldValue(columnDef.field, property);
                                }

                                const val = {value: values[property], color: color};
                                distributionResult.push(val);
                            }
                        }
                        return distributionResult;
                    }, showPercentages)(rowValue, column)
                }).bind(this)
            }
        ,
        ["volume"]:
            {
                width: 'max-content',
                sortable: true,
                editorLabel: 'topics.table.volume',
                headerRenderer: volumeHeaderRenderer(),
                dataRenderer: ((rowValue, column: TableDatasetDisplay<any>) => {
                    return <>{volumeRenderer(rowValue, column, false)}</>
                }).bind(this),
                compareFunction:
                    AbstractTableVizComponent.defaultCompareFunction((item) => {
                        return item.value.count;
                    })
            }
        ,
        ["volume_percent"]: {
            sortable: true,
            editorLabel: 'topics.table.weight',
            headerRenderer: labelHeader('topics.table.weight', true),
            dataRenderer: ((rowValue, column: TableDatasetDisplay<any>) => {
                const percentValue = this.getVolume(rowValue).percent;
                return <>{horizontalBar((rowValue, column) => {
                    return [{
                        percent: percentValue,
                        color: 'grey'
                    }];
                }, false, {height: '9px'})(rowValue, column)}
                    {percentRenderer(percentValue, column)}</>
            }).bind(this),
            compareFunction:
                AbstractTableVizComponent.defaultCompareFunction((item) => {
                    return this.getVolume(item).percent;
                })
        },
        ["volume_over_corpus"]: {
            sortable: true,
            editorLabel: 'topics.table.volumePercent',
            headerRenderer: volumePercentHeaderRenderer(),
            dataRenderer: ((rowValue, column: TableDatasetDisplay<any>) => {
                const percentValue = this.getVolume(rowValue).count * 100 / this.viz.data.totalCount;
                return <>{horizontalBar((rowValue, column) => {
                    return [{
                        percent: percentValue,
                        color: 'grey'
                    }];
                }, false, {height: '9px'})(rowValue, column)}
                    {percentRenderer(percentValue, column)}</>
            }).bind(this),
            compareFunction:
                AbstractTableVizComponent.defaultCompareFunction((item) => {
                    return this.getVolume(item).count * 100 / this.viz.data.totalCount;
                })
        },
        ["volume_trend"]:
            {
                sortable: true,
                editorLabel: 'dashboard.viz.table.trend',
                headerRenderer: labelHeader('dashboard.viz.table.trend', true),
                dataRenderer:
                numberTrendRenderer,
                compareFunction:
                    AbstractTableVizComponent.defaultCompareFunction((item) => {
                        return item.value;
                    })
            }
        ,
        ["satScore"]:
            {
                sortable: true,
                editorLabel: 'dashboard.viz.table.satScore',
                headerRenderer: labelHeader('dashboard.viz.table.satScore', true),
                dataRenderer: satScoreRenderer,
                compareFunction:
                    AbstractTableVizComponent.defaultCompareFunction((item) => {
                        return item.value.sat;
                    })
            }
        ,
        ["tonalities"]:
            {
                sortable: false,
                editorLabel: 'dashboard.viz.table.tonalities',
                headerRenderer: labelHeader('dashboard.viz.table.tonalities', false),
                dataRenderer: this.opinionsAndTonalitiesDataRenderer.bind(this),
                valueGetters:
                    {
                        "positive":
                            (item) => item.value.positive,
                        "negative":
                            (item) => item.value.negative,
                        "neutral":
                            (item) => item.value.neutral
                    }
            }
        ,
        ["net_sentiment"]:
            {
                sortable: false,
                editorLabel: 'dashboard.viz.table.netSentiment',
                headerRenderer: labelHeader('dashboard.viz.table.netSentiment', true),
                dataRenderer:
                    (rowValue, column: TableDatasetDisplay<any>) => {
                        const columnValue = rowValue.data.filter(columnValue => columnValue.name === 'tonalities').find(() => true);
                        if (!columnValue) return null;
                        const values = columnValue.value;
                        const positive = DataRenderer.getPropertyIgnoreCase(values, 'POSITIVE') ? DataRenderer.getPropertyIgnoreCase(values, 'POSITIVE') : 0
                        const negative = DataRenderer.getPropertyIgnoreCase(values, 'NEGATIVE') ? DataRenderer.getPropertyIgnoreCase(values, 'NEGATIVE') : 0
                        const net_sentiment = positive - negative
                        const colorClass = net_sentiment <= -25 ? 'negative' : net_sentiment < 0 ? 'warning' : 'positive';
                        return <div className={classnames('net_sentiment', colorClass)}>{net_sentiment}</div>;
                    },
                width: 'max-content',
                compareFunction:
                    AbstractTableVizComponent.defaultCompareFunction((item) => {
                        const columnValue = item.data.filter(columnValue => columnValue.name === 'tonalities').find(() => true);
                        const values = columnValue.value;
                        const positive = DataRenderer.getPropertyIgnoreCase(values, 'POSITIVE') ? DataRenderer.getPropertyIgnoreCase(values, 'POSITIVE') : 0
                        const negative = DataRenderer.getPropertyIgnoreCase(values, 'NEGATIVE') ? DataRenderer.getPropertyIgnoreCase(values, 'NEGATIVE') : 0
                        const net_sentiment = positive - negative
                        return net_sentiment
                    })
            }
    };


    opinionsAndTonalitiesDataRenderer(rowValue, column: TableDatasetDisplay<any>) {
        const showPercentages = this.viz.definition.uiParams.showPercentages || false;
        const tooltipRenderer = (rowValue, column) => {
            const values = this.opinionsAndTonalitiesValueProvider(rowValue, column.columnName);

            return <div className="tooltip">
                {values.map((item) => (<>
                        <div className="tooltip-label">
                            <span className="tooltip-dot" style={{backgroundColor: item.color}}/>
                            <span>{item.label}</span>
                        </div>
                        <span className="tooltip-value">{item.value}</span>
                        <span className="tooltip-percent">{_.round(item.percent, 2)}%</span>
                    </>
                ))}
            </div>
        };
        const valueProvider = (rowValue, column) => {
            return this.opinionsAndTonalitiesValueProvider(rowValue, column.columnName);
        };
        return horizontalBar(valueProvider, showPercentages, null, tooltipRenderer)(rowValue, column);
    }

    opinionsAndTonalitiesValueProvider(rowValue, columnName: string) {
        const columnValue = rowValue.data.filter(columnValue => columnValue.name === columnName).find(() => true);
        let result = [];
        if (columnValue && columnValue.value) {
            const values: { [key: string]: number } = columnValue.value;
            const total = Object.entries(values).map(o => o[1]).reduce(
                (accumulator, currentValue) => accumulator + currentValue);

            const positive = DataRenderer.getPropertyIgnoreCase(values, 'POSITIVE') ? DataRenderer.getPropertyIgnoreCase(values, 'POSITIVE') : 0
            const negative = DataRenderer.getPropertyIgnoreCase(values, 'NEGATIVE') ? DataRenderer.getPropertyIgnoreCase(values, 'NEGATIVE') : 0
            const neutral = DataRenderer.getPropertyIgnoreCase(values, 'NEUTRAL') ? DataRenderer.getPropertyIgnoreCase(values, 'NEUTRAL') : 0
            result = [
                {
                    label: this.t('globalSatisfaction.POSITIVE'),
                    value: positive,
                    color: colors.positive,
                    percent: 100 * (positive) / total
                },
                {
                    label: this.t('globalSatisfaction.NEUTRAL'),
                    value: neutral,
                    color: colors.neutral,
                    percent: 100 * neutral / total
                },
                {
                    label: this.t('globalSatisfaction.NEGATIVE'),
                    value: negative,
                    color: colors.negative,
                    percent: 100 * negative / total
                }];

        }
        return result;
    }

    static getPropertyIgnoreCase(obj, property) {
        const key = Object.keys(obj).find(
            k => k.toLowerCase() === property.toLowerCase()
        );

        return key !== undefined ? obj[key] : undefined;
    }

    protected getVolume(item: TableItem): { count: number, percent: number } {
        return item.data.find(
            x => x.name === "volume"
        )?.value;
    }
}